import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Grid,
  Card,
  CardContent,
  Pagination,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  GetApp as ExportIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { vi } from "date-fns/locale";
import { toast } from "react-toastify";
import {
  getSubmissions,
  getSubmissionById,
  updateSubmissionStatus,
  deleteSubmission,
  exportSubmissionsToExcel,
  getSubmissionStats,
} from "../../services/FormService";

const SUBMISSION_STATUS = [
  { value: "pending", label: "Chờ xử lý", color: "warning" },
  { value: "processed", label: "Đã xử lý", color: "success" },
  { value: "archived", label: "Đã lưu trữ", color: "default" },
];

const SubmissionViewer = ({ formConfig, onClose }) => {
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    status: "",
    search: "",
    startDate: null,
    endDate: null,
    page: 1,
    limit: 20,
  });

  const [pagination, setPagination] = useState({
    current: 1,
    total: 1,
    count: 0,
    totalItems: 0,
  });

  useEffect(() => {
    if (formConfig) {
      loadSubmissions();
      loadStats();
    }
  }, [formConfig, filters]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const params = {
        formConfigId: formConfig._id,
        ...filters,
        startDate: filters.startDate ? filters.startDate.toISOString() : null,
        endDate: filters.endDate ? filters.endDate.toISOString() : null,
      };

      // Remove empty values
      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === null) {
          delete params[key];
        }
      });

      const response = await getSubmissions(params);
      if (response.success) {
        setSubmissions(response.data.submissions);
        setPagination(response.data.pagination);
      } else {
        toast.error("Lỗi khi tải submissions");
      }
    } catch (error) {
      console.error("Error loading submissions:", error);
      toast.error("Lỗi khi tải submissions");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const params = {
        formConfigId: formConfig._id,
        startDate: filters.startDate ? filters.startDate.toISOString() : null,
        endDate: filters.endDate ? filters.endDate.toISOString() : null,
      };

      // Remove empty values
      Object.keys(params).forEach((key) => {
        if (params[key] === null) {
          delete params[key];
        }
      });

      const response = await getSubmissionStats(params);
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to first page when filtering
    }));
  };

  const handlePageChange = (event, newPage) => {
    setFilters((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleViewSubmission = async (submission) => {
    try {
      const response = await getSubmissionById(submission._id);
      if (response.success) {
        setSelectedSubmission(response.data);
        setShowDetailDialog(true);
      } else {
        toast.error("Lỗi khi tải chi tiết submission");
      }
    } catch (error) {
      console.error("Error loading submission detail:", error);
      toast.error("Lỗi khi tải chi tiết submission");
    }
  };

  const handleStatusChange = async (submissionId, newStatus, notes = "") => {
    try {
      const response = await updateSubmissionStatus(
        submissionId,
        newStatus,
        notes
      );
      if (response.success) {
        toast.success("Cập nhật trạng thái thành công");
        loadSubmissions();
        loadStats();
        if (selectedSubmission && selectedSubmission._id === submissionId) {
          setSelectedSubmission((prev) => ({
            ...prev,
            status: newStatus,
            notes,
          }));
        }
      } else {
        toast.error("Lỗi khi cập nhật trạng thái");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Lỗi khi cập nhật trạng thái");
    }
  };

  const handleDeleteSubmission = (submission) => {
    setSubmissionToDelete(submission);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteSubmission(submissionToDelete._id);
      if (response.success) {
        toast.success("Xóa submission thành công");
        loadSubmissions();
        loadStats();
      } else {
        toast.error("Lỗi khi xóa submission");
      }
    } catch (error) {
      console.error("Error deleting submission:", error);
      toast.error("Lỗi khi xóa submission");
    } finally {
      setShowDeleteDialog(false);
      setSubmissionToDelete(null);
    }
  };

  const handleExportExcel = async () => {
    try {
      const params = {
        formConfigId: formConfig._id,
        ...filters,
        startDate: filters.startDate ? filters.startDate.toISOString() : null,
        endDate: filters.endDate ? filters.endDate.toISOString() : null,
      };

      // Remove pagination and empty values
      delete params.page;
      delete params.limit;
      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === null) {
          delete params[key];
        }
      });

      const blob = await exportSubmissionsToExcel(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `submissions_${formConfig.name}_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Xuất Excel thành công");
    } catch (error) {
      console.error("Error exporting Excel:", error);
      toast.error("Lỗi khi xuất Excel");
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = SUBMISSION_STATUS.find((s) => s.value === status);
    return (
      <Chip
        label={statusConfig?.label || status}
        color={statusConfig?.color || "default"}
        size="small"
      />
    );
  };

  const renderSubmissionValue = (field, value) => {
    if (!value) return "-";

    if (Array.isArray(value)) {
      return value.join(", ");
    }

    if (typeof value === "object") {
      return JSON.stringify(value);
    }

    if (field.type === "date") {
      return new Date(value).toLocaleDateString("vi-VN");
    }

    return value.toString();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
      <Box>
        {/* Stats Cards */}
        {stats && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Tổng submissions
                  </Typography>
                  <Typography variant="h4">
                    {stats.summary.totalSubmissions}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Chờ xử lý
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.summary.pendingCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Đã xử lý
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {stats.summary.processedCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Lưu trữ
                  </Typography>
                  <Typography variant="h4" color="text.secondary">
                    {stats.summary.archivedCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Tìm kiếm"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
                placeholder="Email, tên..."
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Trạng thái</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  label="Trạng thái"
                >
                  <MenuItem value="">Tất cả</MenuItem>
                  {SUBMISSION_STATUS.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="Từ ngày"
                value={filters.startDate}
                onChange={(date) => handleFilterChange("startDate", date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="Đến ngày"
                value={filters.endDate}
                onChange={(date) => handleFilterChange("endDate", date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={3}>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  onClick={() =>
                    setFilters({
                      status: "",
                      search: "",
                      startDate: null,
                      endDate: null,
                      page: 1,
                      limit: 20,
                    })
                  }
                  size="small"
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  startIcon={<ExportIcon />}
                  onClick={handleExportExcel}
                  size="small"
                >
                  Xuất Excel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Submissions Table */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : submissions.length === 0 ? (
          <Alert severity="info">Không có submission nào.</Alert>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Thời gian</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Tên</TableCell>
                    <TableCell>Trạng thái</TableCell>
                    <TableCell>Thao tác</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submissions.map((submission) => (
                    <TableRow key={submission._id}>
                      <TableCell>
                        {new Date(submission.submittedAt).toLocaleString(
                          "vi-VN"
                        )}
                      </TableCell>
                      <TableCell>
                        {submission.submitterInfo?.email ||
                          submission.submittedBy?.email ||
                          "-"}
                      </TableCell>
                      <TableCell>
                        {submission.submitterInfo?.fullName ||
                          submission.submittedBy?.fullName ||
                          "-"}
                      </TableCell>
                      <TableCell>{getStatusChip(submission.status)}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleViewSubmission(submission)}
                          color="primary"
                          size="small"
                          title="Xem chi tiết"
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteSubmission(submission)}
                          color="error"
                          size="small"
                          title="Xóa"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={pagination.total}
                page={pagination.current}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        )}

        {/* Submission Detail Dialog */}
        <Dialog
          open={showDetailDialog}
          onClose={() => setShowDetailDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Chi tiết Submission</DialogTitle>
          <DialogContent>
            {selectedSubmission && (
              <Box>
                {/* Basic Info */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Thời gian gửi:</Typography>
                    <Typography>
                      {new Date(selectedSubmission.submittedAt).toLocaleString(
                        "vi-VN"
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Trạng thái:</Typography>
                    {getStatusChip(selectedSubmission.status)}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Email:</Typography>
                    <Typography>
                      {selectedSubmission.submitterInfo?.email ||
                        selectedSubmission.submittedBy?.email ||
                        "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Tên:</Typography>
                    <Typography>
                      {selectedSubmission.submitterInfo?.fullName ||
                        selectedSubmission.submittedBy?.fullName ||
                        "-"}
                    </Typography>
                  </Grid>
                  {selectedSubmission.submitterInfo?.ipAddress && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2">IP Address:</Typography>
                      <Typography>
                        {selectedSubmission.submitterInfo.ipAddress}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                {/* Form Data */}
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Dữ liệu Form
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Field</TableCell>
                        <TableCell>Giá trị</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formConfig.fields.map((field) => {
                        const value =
                          selectedSubmission.submittedData?.[field.id];
                        return (
                          <TableRow key={field.id}>
                            <TableCell>
                              <Typography variant="subtitle2">
                                {field.label}
                                {field.required && (
                                  <span style={{ color: "red" }}> *</span>
                                )}
                              </Typography>
                              {field.description && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {field.description}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {renderSubmissionValue(field, value)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Status Update */}
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Cập nhật trạng thái
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Trạng thái mới</InputLabel>
                      <Select
                        value={selectedSubmission.status}
                        onChange={(e) =>
                          handleStatusChange(
                            selectedSubmission._id,
                            e.target.value,
                            selectedSubmission.notes
                          )
                        }
                        label="Trạng thái mới"
                      >
                        {SUBMISSION_STATUS.map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            {status.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Ghi chú"
                      value={selectedSubmission.notes || ""}
                      onChange={(e) => {
                        setSelectedSubmission((prev) => ({
                          ...prev,
                          notes: e.target.value,
                        }));
                      }}
                      onBlur={(e) =>
                        handleStatusChange(
                          selectedSubmission._id,
                          selectedSubmission.status,
                          e.target.value
                        )
                      }
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Thêm ghi chú..."
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDetailDialog(false)}>Đóng</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
        >
          <DialogTitle>Xác nhận xóa</DialogTitle>
          <DialogContent>
            <Typography>
              Bạn có chắc chắn muốn xóa submission này? Hành động này không thể
              hoàn tác.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteDialog(false)}>Hủy</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Xóa
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default SubmissionViewer;
